// The document footer — rep-only booked margin on the left, the money on the
// right, with the Hide-totals control. Matches the commonsku layout the CEO
// approved (design preview ef96cb3f).
//
// "Hide totals" is `orders.hide_pricing` (estimate-only, round 22). On the
// client-facing ESTIMATE it now hides ONLY the totals block — the server nulls
// the totals payload so the numbers aren't merely hidden in the DOM — while
// per-item / per-colour / decoration-charge line pricing stays visible
// (CEO 2026-07-27: hiding totals means hiding the TOTAL, not line pricing). It
// was simply never surfaced on the rep page — the CEO asked for it "just like
// commonsku".
import { formatCents, formatPct } from "./money";
import { cn } from "@/lib/utils";

export interface DocumentTotalsProps {
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  currency?: string;
  /** Tax code shown against the tax row ("E", "FL"). */
  taxCode?: string | null;
  /** When set, shipping is broken out on its own row and SUBTRACTED from the
   *  displayed subtotal, so Subtotal + Tax + Shipping = Total still reads true.
   *  The client-facing document has always split shipping out this way. */
  shippingCents?: number | null;
  /** Rep-only: ALL-INCLUSIVE booked margin (goods + decoration/additional charges
   *  + shipping, both sides — CEO 2026-07-25, the commission basis definition).
   *  Omit on client-facing views. */
  bookedMarginCents?: number | null;
  bookedMarginPct?: number | null;
  /** Renders the Hide-totals control. Omit for read-only/client views. */
  hideTotals?: boolean;
  onToggleHideTotals?: (next: boolean) => void;
  disabled?: boolean;
  /** "panel" = the handoff's right-hand Client total + Internal view; the
   *  default "footer" is the original under-the-lines layout. */
  variant?: "footer" | "panel";
  /** Panel only: say "Not estimated" for shipping when no shipping line exists. */
  shippingMissing?: boolean;
  /** Panel only: the card fee the client pays on the upfront payment
   *  (Stephanie 2026-10-26). Shown under Total with the total including it;
   *  the quote's own Total stays what gets invoiced. */
  cardFee?: { cents: number; pct: number | null } | null;
}

export function DocumentTotals(props: DocumentTotalsProps) {
  const {
    subtotalCents,
    taxCents,
    totalCents,
    currency = "USD",
    taxCode,
    shippingCents,
    bookedMarginCents,
    bookedMarginPct,
    hideTotals,
    onToggleHideTotals,
    disabled,
    variant = "footer",
    shippingMissing,
    cardFee,
  } = props;

  const showBooked = bookedMarginCents != null || bookedMarginPct != null;
  // Shipping lines are inside the server's subtotal; showing them on their own
  // row means the "Subtotal" line must exclude them or the column won't add up.
  const shipping = shippingCents ?? 0;
  const goodsSubtotalCents = subtotalCents - shipping;

  if (variant === "panel") {
    const row = "my-[13px] flex items-center justify-between gap-4 text-sm tabular-nums";
    return (
      <div>
        <section className="rounded-md border border-rule bg-white p-[22px]">
          <h2 className="mb-4 text-lg font-semibold tracking-[-0.4px] text-ink">Client total</h2>
          <div className={row}><span className="text-muted">Subtotal</span><span className="font-medium text-ink">{formatCents(goodsSubtotalCents, currency)}</span></div>
          {/* No tax → no tax line (Stephanie 2026-10-13: "Tax (FL) $0.00" on an
              out-of-state order was confusing). Same on the client's copy.
              Tax sits after the items and before shipping — shipping is tax
              exempt (Stephanie 2026-10-14). */}
          {taxCents !== 0 && (
            <div className={row}><span className="text-muted">{taxCode && taxCode.trim() !== "" ? `Sales tax (${taxCode})` : "Sales tax"}</span><span className="font-medium text-ink">{formatCents(taxCents, currency)}</span></div>
          )}
          <div className={row}>
            <span className="text-muted">Shipping</span>
            <span className="font-medium text-ink">{shippingMissing ? "Not estimated" : formatCents(shipping, currency)}</span>
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-rule pt-[15px] text-[19px] font-semibold tabular-nums text-ink">
            <span>Total</span>
            <span>{formatCents(totalCents, currency)}</span>
          </div>
          {cardFee && cardFee.cents > 0 && (
            <>
              <div className={row}>
                <span className="text-muted">{cardFee.pct != null ? `Card fee (${cardFee.pct}%)` : "Card fee"}</span>
                <span className="font-medium text-ink">{formatCents(cardFee.cents, currency)}</span>
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-rule pt-[13px] text-[15px] font-semibold tabular-nums text-ink">
                <span>Total with card fee</span>
                <span>{formatCents(totalCents + cardFee.cents, currency)}</span>
              </div>
            </>
          )}
          {onToggleHideTotals && (
            <label className={cn("mt-4 flex cursor-pointer items-start gap-2 text-xs text-muted", disabled && "cursor-not-allowed opacity-60")}>
              <input
                type="checkbox"
                checked={hideTotals ?? false}
                disabled={disabled}
                onChange={(e) => onToggleHideTotals(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-rule text-ink"
              />
              <span><span className="font-medium text-ink">Hide totals</span> on the client’s copy — line prices still show.</span>
            </label>
          )}
        </section>
        {showBooked && (
          <section className="mt-6 rounded-md border border-rule bg-white p-[22px]">
            <h2 className="mb-2 text-lg font-semibold tracking-[-0.4px] text-ink">Internal view</h2>
            <p className="text-xs text-muted">Margin is for the team only — never part of the client document.</p>
            <div className={row}><span className="text-muted">Booked margin</span><span className="font-medium text-ink">{bookedMarginCents != null ? formatCents(bookedMarginCents, currency) : "—"}</span></div>
            <div className={row}><span className="text-muted">Margin %</span><span className="font-medium text-ink">{formatPct(bookedMarginPct ?? null)}</span></div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-5 px-4 py-4 sm:grid-cols-2">
      {/* Booked margin — rep-only, never on a client copy */}
      <div className="self-start">
        {showBooked && (
          <div className="overflow-hidden rounded-md">
            <div className="flex items-center justify-between gap-3 bg-brown-50 px-3 py-1.5 text-[12.5px] font-bold tabular-nums text-brown-400">
              <span>Booked margin amount</span>
              <span>{bookedMarginCents != null ? formatCents(bookedMarginCents, currency) : "—"}</span>
            </div>
            <div className="mt-px flex items-center justify-between gap-3 bg-brown-50 px-3 py-1.5 text-[12.5px] font-bold tabular-nums text-brown-400">
              <span>Booked margin</span>
              <span>{formatPct(bookedMarginPct ?? null)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Money */}
      <div>
        <div className="overflow-hidden rounded-md">
          <div className="flex items-center justify-between gap-3 bg-cream-50 px-3 py-1.5 text-[13px] tabular-nums text-brown-400">
            <span>Subtotal</span>
            <span>{formatCents(goodsSubtotalCents, currency)}</span>
          </div>
          {taxCents !== 0 && (
            <div className="mt-px flex items-center justify-between gap-3 bg-cream-50 px-3 py-1.5 text-[13px] tabular-nums text-brown-400">
              <span>{taxCode && taxCode.trim() !== "" ? `Sales tax (${taxCode})` : "Sales tax"}</span>
              <span>{formatCents(taxCents, currency)}</span>
            </div>
          )}
          {shipping > 0 && (
            <div className="mt-px flex items-center justify-between gap-3 bg-cream-50 px-3 py-1.5 text-[13px] tabular-nums text-brown-400">
              <span>Shipping</span>
              <span>{formatCents(shipping, currency)}</span>
            </div>
          )}
          <div className="mt-px flex items-center justify-between gap-3 bg-cream-50 px-3 py-2 text-[15.5px] font-semibold tabular-nums text-brown-500">
            <span>Total</span>
            <span>{formatCents(totalCents, currency)}</span>
          </div>
        </div>

        {onToggleHideTotals && (
          <label
            className={cn(
              "mt-2 flex cursor-pointer items-start gap-2 px-1 text-[12.5px] font-semibold text-brown-400",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            <input
              type="checkbox"
              checked={hideTotals ?? false}
              disabled={disabled}
              onChange={(e) => onToggleHideTotals(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 rounded border-brown-300 text-rust-500 focus:ring-2 focus:ring-peach-200"
            />
            <span className="shrink-0">Hide totals</span>
            <span className="min-w-0 font-normal text-brown-300">— hides the totals block on the client’s copy; line prices still show</span>
          </label>
        )}
      </div>
    </div>
  );
}
